import { useState } from 'react'
import type { MachineView, PersonView } from './derive'
import { Avatar, NetDot } from './parts'

/** A searchable checklist. Used for picking people into a team and machines into a group. */
function Checklist<T extends { id: string }>({
  items,
  chosen,
  onChange,
  placeholder,
  match,
  render,
}: {
  items: readonly T[]
  chosen: ReadonlySet<string>
  onChange: (next: ReadonlySet<string>) => void
  placeholder: string
  match: (item: T, needle: string) => boolean
  render: (item: T) => React.ReactNode
}): JSX.Element {
  const [q, setQ] = useState('')
  const needle = q.trim().toLowerCase()
  const shown = needle ? items.filter((i) => match(i, needle)) : items
  const toggle = (id: string): void => {
    const next = new Set(chosen)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange(next)
  }
  const allShown = shown.length > 0 && shown.every((i) => chosen.has(i.id))
  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="row" style={{ gap: 8 }}>
        <input className="cx-search" placeholder={placeholder} value={q} onChange={(e) => setQ(e.currentTarget.value)} />
        <button
          onClick={() => {
            const next = new Set(chosen)
            for (const i of shown) (allShown ? next.delete(i.id) : next.add(i.id))
            onChange(next)
          }}
        >
          {allShown ? 'Clear shown' : `All ${shown.length} shown`}
        </button>
      </div>
      <ul className="cx-checklist">
        {shown.map((i) => (
          <li key={i.id}>
            <label className="cx-checklist__row">
              <input type="checkbox" checked={chosen.has(i.id)} onChange={() => toggle(i.id)} />
              {render(i)}
            </label>
          </li>
        ))}
      </ul>
      <span className="muted">{chosen.size} chosen</span>
    </div>
  )
}

export function PeoplePicker(props: { people: readonly PersonView[]; chosen: ReadonlySet<string>; onChange: (next: ReadonlySet<string>) => void }): JSX.Element {
  return (
    <Checklist
      items={props.people}
      chosen={props.chosen}
      onChange={props.onChange}
      placeholder="Find a person"
      match={(p, n) => p.name.toLowerCase().includes(n) || p.email.toLowerCase().includes(n)}
      render={(p) => (
        <>
          <Avatar initials={p.initials} online={p.online} size={28} />
          <span className="stack" style={{ gap: 0 }}>
            <span>{p.name}</span>
            <span className="muted cx-small">{p.email}</span>
          </span>
        </>
      )}
    />
  )
}

export function MachinePicker(props: { machines: readonly MachineView[]; chosen: ReadonlySet<string>; onChange: (next: ReadonlySet<string>) => void }): JSX.Element {
  return (
    <Checklist
      items={props.machines}
      chosen={props.chosen}
      onChange={props.onChange}
      placeholder="Find a machine, a site or an address"
      match={(m, n) =>
        m.name.toLowerCase().includes(n) || m.tag.includes(n) || (m.robot?.site ?? '').toLowerCase().includes(n) || (m.device?.addresses[0] ?? '').includes(n)
      }
      render={(m) => (
        <>
          <NetDot state={m.net} />
          <span className="cx-mono">{m.name}</span>
          <span className="muted cx-small">{m.robot?.site || (m.kind === 'computer' ? 'office computer' : '')}</span>
        </>
      )}
    />
  )
}
